import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Search from 'components/shared/Search';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { DischargeChecklist, DischargeChecklistItem } from 'models/DischargeChecklistItem';
import { PERMISSIONS } from 'models/Profile';
import {
  dischargeChecklistQueryKeys,
  indexDischargeChecklists,
  reorderDischargeChecklistItems,
} from 'services/api/dischargeChecklists';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import DeleteDischargeChecklistItemModal from './DeleteDischargeChecklistItemModal';
import DischargeChecklistItemModal from './DischargeChecklistItemModal';
import DischargeChecklistTable from './DischargeChecklistTable';

type ActiveModal =
  | { type: 'add' }
  | { type: 'edit'; item: DischargeChecklistItem }
  | { type: 'delete'; item: DischargeChecklistItem };

export type DischargeChecklistListProps = {
  // Absent means the acting client's own lists (Configuration). Present means an Olio admin is
  // looking at that client's lists from Admin > Clients.
  clientId?: string;
  isAdmin?: boolean;
  title?: string;
  onBack?: () => void;
};

export default function DischargeChecklistList({
  clientId,
  isAdmin = false,
  title,
  onBack,
}: DischargeChecklistListProps) {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [selectedGroupTypeId, setSelectedGroupTypeId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  const canEdit = profile.has(isAdmin ? PERMISSIONS.adminDischargeChecklistEdit : PERMISSIONS.dischargeChecklistEdit);
  const queryKey = dischargeChecklistQueryKeys.index(clientId);

  const {
    data: lists = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: ({ signal }) => indexDischargeChecklists(clientId, signal),
  });

  const tabs: TabType[] = lists.map((list) => ({ label: list.groupType.name, value: list.groupType.id }));
  // Falls back to the first tab when the chosen one goes away (Discharge Planning turned off).
  const selectedList = lists.find((list) => list.groupType.id === selectedGroupTypeId) ?? lists[0];
  const selectedTab = tabs.find((tab) => tab.value === selectedList?.groupType.id);

  const term = search.trim().toLowerCase();
  const visibleItems = useMemo(
    () => (selectedList?.items ?? []).filter((item) => item.label.toLowerCase().includes(term)),
    [selectedList, term]
  );

  // Saved on drop. The new order shows right away and goes back if the save fails.
  const reorder = useMutation({
    mutationFn: reorderDischargeChecklistItems,
    onMutate: async ({ groupTypeId, items }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DischargeChecklist[]>(queryKey);
      const order = new Map(items.map(({ id, position }) => [id, position]));

      queryClient.setQueryData<DischargeChecklist[]>(queryKey, (current = []) =>
        current.map((list) =>
          list.groupType.id === groupTypeId
            ? {
                ...list,
                items: [...list.items]
                  .map((item) => ({ ...item, position: order.get(item.id) ?? item.position }))
                  .sort((a, b) => a.position - b.position),
              }
            : list
        )
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      addToast({ text: 'The new order could not be saved. Please try again.' });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: dischargeChecklistQueryKeys.base }),
  });

  const handleMove = (fromIndex: number, toIndex: number) => {
    if (!selectedList || fromIndex === toIndex) return;

    const reordered = [...selectedList.items];
    const [moved] = reordered.splice(fromIndex, 1);

    reordered.splice(toIndex, 0, moved);
    reorder.mutate({
      clientId,
      groupTypeId: selectedList.groupType.id,
      items: reordered.map((item, position) => ({ id: item.id, position })),
    });
  };

  const closeModal = () => setActiveModal(null);

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>{title ?? 'Discharge Checklist'}</Title>
            <SearchContainer>
              <Search value={search} onChange={setSearch} placeholder='Search' />
            </SearchContainer>
          </TitleGroup>
          <HeaderActions>
            {onBack && (
              <Button variant='ghost' onClick={onBack}>
                Back to Clients
              </Button>
            )}
            {canEdit && selectedList && (
              <Button onClick={() => setActiveModal({ type: 'add' })} data-testid='add-checklist-item'>
                <PlusIcon width={16} height={16} />
                Add Item
              </Button>
            )}
          </HeaderActions>
        </HeaderRow>
        {tabs.length > 0 && (
          <TabBar>
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedGroupTypeId(tab.value)} />
          </TabBar>
        )}
      </Page.Header>

      <Page.Content>
        {isLoading ? (
          <CircularProgress />
        ) : isError && !lists.length ? (
          <Description>The discharge checklist could not be loaded. Please refresh to try again.</Description>
        ) : !selectedList ? (
          <Description>Turn on Discharge Planning for a level of care to set up its discharge checklist.</Description>
        ) : (
          <>
            <Description>
              Manage checklist items your team needs to confirm before a discharge. These checklist items will be
              displayed and updated in the Discharge Planning section of the Patient Story. The Discharge Calendar will
              display how many checklist items are complete for each patient prior to discharge.
            </Description>
            <DischargeChecklistTable
              items={visibleItems}
              canEdit={canEdit}
              canReorder={canEdit && !term}
              isSavingOrder={reorder.isPending}
              onMove={handleMove}
              onEdit={(item) => setActiveModal({ type: 'edit', item })}
              onDelete={(item) => setActiveModal({ type: 'delete', item })}
            />
          </>
        )}
      </Page.Content>

      {selectedList && (activeModal?.type === 'add' || activeModal?.type === 'edit') && (
        <DischargeChecklistItemModal
          item={activeModal.type === 'edit' ? activeModal.item : undefined}
          groupTypeId={selectedList.groupType.id}
          clientId={clientId}
          onClose={closeModal}
        />
      )}

      {activeModal?.type === 'delete' && (
        <DeleteDischargeChecklistItemModal item={activeModal.item} clientId={clientId} onClose={closeModal} />
      )}
    </Page>
  );
}

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchContainer = styled.div`
  width: 200px;
`;

const Title = styled(H2)`
  font-size: 24px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-top: 1px solid ${colors.black10};
`;

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;
